import { Node, mergeAttributes } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

// A formula that is written where it stands, the way Mathcha does it: the math-field is live inside
// the document, not a picture of one opened in a dialog. Typing in it is MathLive's - \frac, ^, _,
// arrows through the structure - and leaving it (an arrow past its edge, Enter, Escape) puts the
// caret back in the text on the side it left from.
//
// Two nodes share the one view: inline, inside a sentence, and display, a formula on a line of its
// own. Both save as the same <math-field data-latex="…"> the rest of Examin already reads, the
// display one wrapped in a <div data-math-display>.

// A formula just made gets the caret: set by the command that inserts it, taken by the next view
// drawn - an insert draws only the new node's.
let focusNext = false;
export function focusNextFormula(): void { focusNext = true; }

type Direction = "forward" | "backward";

function leave(view: EditorView, getPos: () => number | undefined, size: number, direction: Direction): void {
  const pos = getPos();
  if (pos === undefined) return;
  const target = direction === "forward" ? pos + size : pos;
  const $pos = view.state.doc.resolve(Math.min(target, view.state.doc.content.size));
  const selection = TextSelection.near($pos, direction === "forward" ? 1 : -1);
  view.dispatch(view.state.tr.setSelection(selection).scrollIntoView());
  view.focus();
}

function remove(view: EditorView, getPos: () => number | undefined, size: number): void {
  const pos = getPos();
  if (pos === undefined) return;
  const tr = view.state.tr.delete(pos, pos + size);
  tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(pos, tr.doc.content.size)), -1));
  view.dispatch(tr.scrollIntoView());
  view.focus();
}

function formulaView(display: boolean) {
  return ({ node, getPos, editor }: any) => {
    const view: EditorView = editor.view;
    const field = document.createElement("math-field") as any;
    field.setAttribute("data-latex", node.attrs.latex);
    field.classList.add("live-math");
    let current = node;

    // Always wrapped: ProseMirror marks an atom's own element contenteditable="false", and MathLive
    // reads that on a math-field as read-only - nothing typed would reach it.
    const dom = document.createElement(display ? "div" : "span");
    dom.className = display ? "live-math-display" : "live-math-inline";
    if (display) dom.setAttribute("data-math-display", "");
    dom.appendChild(field);

    customElements.whenDefined("math-field").then(() => {
      field.value = current.attrs.latex;
      field.smartFence = true;
      field.letterShapeStyle = "tex";
      field.mathVirtualKeyboardPolicy = "manual";
      field.readOnly = !editor.isEditable;
      if (focusNext) {
        focusNext = false;
        requestAnimationFrame(() => field.focus());
      }
    });

    // What is typed goes back into the document, so saving, undo in the text around it and the
    // copy kept on the device all see the formula as it is.
    field.addEventListener("input", () => {
      const pos = getPos();
      if (pos === undefined) return;
      const latex = field.value || "";
      if (latex === current.attrs.latex) return;
      view.dispatch(view.state.tr.setNodeMarkup(pos, undefined, { ...current.attrs, latex }));
    });

    field.addEventListener("move-out", (event: CustomEvent) => {
      event.preventDefault();
      const d = event.detail?.direction;
      leave(view, getPos, current.nodeSize, d === "backward" || d === "upward" ? "backward" : "forward");
    });

    field.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "Escape" || (event.key === "Enter" && !event.shiftKey)) {
        event.preventDefault();
        event.stopPropagation();
        leave(view, getPos, current.nodeSize, "forward");
      } else if ((event.key === "Backspace" || event.key === "Delete") && !(field.value || "").trim()) {
        event.preventDefault();
        event.stopPropagation();
        remove(view, getPos, current.nodeSize);
      }
    }, true);

    field.addEventListener("focusin", () => dom.classList.add("is-editing"));
    field.addEventListener("focusout", () => dom.classList.remove("is-editing"));

    return {
      dom,
      // The caret arriving on the formula - an arrow from the text, a click - goes into it.
      selectNode: () => { if (editor.isEditable) field.focus(); },
      deselectNode: () => {},
      update: (updated: any) => {
        if (updated.type !== current.type) return false;
        current = updated;
        field.setAttribute("data-latex", updated.attrs.latex);
        if ((field.value ?? "") !== updated.attrs.latex) field.value = updated.attrs.latex;
        return true;
      },
      // The keys and the changes inside the field are MathLive's, not the document's.
      stopEvent: (event: Event) => field.contains(event.target as any) || event.target === field,
      ignoreMutation: () => true,
    };
  };
}

export const LiveMath = Node.create({
  name: "liveMath",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      latex: {
        default: "",
        parseHTML: (el: HTMLElement) => el.getAttribute("data-latex") || "",
        renderHTML: (attrs: { latex: string }) => ({ "data-latex": attrs.latex }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "math-field[data-latex]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["math-field", mergeAttributes(HTMLAttributes, { "read-only": "", contenteditable: "false" })];
  },

  addNodeView() {
    return formulaView(false);
  },
});

export const LiveMathDisplay = Node.create({
  name: "liveMathDisplay",
  group: "block",
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      latex: {
        default: "",
        parseHTML: (el: HTMLElement) => el.querySelector("math-field")?.getAttribute("data-latex") || "",
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-math-display]", priority: 60 }];
  },

  renderHTML({ node }) {
    return ["div", { "data-math-display": "" },
      ["math-field", { "data-latex": node.attrs.latex, "read-only": "", contenteditable: "false" }]];
  },

  addNodeView() {
    return formulaView(true);
  },
});
